import { Avatar, Checkbox, Collapse, List, ListItem, ListItemButton, ListItemIcon, ListItemText, MenuItem, Popover, Select, TextField } from "@mui/material"
import { useEffect, useState } from "react";
import xslx from "../../assets/images";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import {
  buildPinnedRowBlocksFromData,
  bundleAdditionForProductQtyCol,
  extractBeforeNumber,
  getBundleAdditionsMap,
  isProductQtyColId,
  productIdFromQtyColId,
  resolveProductQtyGridColId,
} from "./ag-grid-functions";
import {getSelectedRows} from "./Ag-Table";
import {
  buildExcelImageMapByProductId,
  excelImageKeyForRow,
  isImageExportColumn,
  parseDataUrl,
} from "./excel-export-images";
import type { ExcelExportImage } from "./excel-export-images";

// ---------------------------------------------------------------------------
// Bundle products support
// ---------------------------------------------------------------------------
// A row may have bundle metadata fields named `aap_X` where X is a product id.
// When present and non-empty, its value is an underscore-separated list of
// product ids (e.g. "2_3"). The amount ordered for product X (stored in `p_X`)
// is also added to each listed product's export cells (`p_2`, `p_3`) via
// processCellCallback. Optionally, export can omit the bundle-source column
// `p_X` / `rrp_X` itself (see includeBundleSourceProductColumns).
// ---------------------------------------------------------------------------

const extractRowData = (nodeOrData: any): any => {
  if (!nodeOrData) return null;
  if (nodeOrData.data && typeof nodeOrData.data === 'object') return nodeOrData.data;
  if (typeof nodeOrData === 'object') return nodeOrData;
  return null;
};

const productQtyColAlreadyExported = (productId: string, baseColumnIds: string[]): boolean =>
  baseColumnIds.some((id) => productIdFromQtyColId(id) === productId);

const getBundleExtraColumnIds = (items: any[], baseColumnIds: string[], gridColIds: string[]): string[] => {
  const extras = new Set<string>();
  for (const item of items || []) {
    const additions = getBundleAdditionsMap(extractRowData(item));
    if (!additions) continue;
    for (const col in additions) {
      const productId = productIdFromQtyColId(col);
      if (!productId || productQtyColAlreadyExported(productId, baseColumnIds)) continue;
      const resolved = resolveProductQtyGridColId(productId, gridColIds);
      if (resolved) extras.add(resolved);
    }
  }
  return Array.from(extras);
};

// Augments baseColumnIds with bundle-destination p_Y columns so that values
// contributed by `aap_X` bundles end up in the exported sheet. Missing columns
// are only added if they exist in the grid; column order follows the grid so
// auto-added p_Y columns sit next to the other p_* columns.
const getBundleAugmentedColumnKeys = (tableRef: any, items: any[], baseColumnIds: string[]): string[] => {
  const allGridCols = tableRef?.current?.columnApi?.getAllGridColumns?.() || [];
  const gridIds: string[] = allGridCols.map((c: any) => c?.getColId?.()).filter(Boolean);
  const extras = getBundleExtraColumnIds(items, baseColumnIds, gridIds);
  if (!extras.length) return baseColumnIds;
  const validExtras = extras.filter((id) => gridIds.includes(id));
  if (!validExtras.length) return baseColumnIds;
  const finalSet = new Set<string>([...baseColumnIds, ...validExtras]);
  const ordered: string[] = [];
  for (const id of gridIds) {
    if (finalSet.has(id)) ordered.push(id);
  }
  for (const id of baseColumnIds) {
    if (!ordered.includes(id)) ordered.push(id);
  }
  for (const id of validExtras) {
    if (!ordered.includes(id)) ordered.push(id);
  }
  return ordered;
};

/** `p_X` / `rrp_X` column ids for products that define a bundle (`aap_X` non-empty) on at least one row in `items`. */
const getBundleSourceProductColumnIdsFromItems = (items: any[]): Set<string> => {
  const out = new Set<string>();
  for (const item of items || []) {
    const d = extractRowData(item);
    if (!d || typeof d !== 'object') continue;
    for (const key of Object.keys(d)) {
      if (!key || key.indexOf('aap_') !== 0) continue;
      const val = d[key];
      if (val == null || String(val).trim() === '') continue;
      const productId = key.slice(4);
      out.add(`p_${productId}`);
      out.add(`rrp_${productId}`);
    }
  }
  return out;
};

/**
 * Default: do not export bundle-source product columns (`p_X` / `rrp_X` when `aap_X` exists on any exported row).
 * Quantities still roll into destination `p_*` via `bundleProcessCellCallback`.
 * When `includeBundleSourceProductColumns` is true, keep those columns in the export.
 */
const applyBundleSourceProductColumnExportPreference = (
  colIds: string[],
  itemsForBundleDetection: any[],
  includeBundleSourceProductColumns: boolean
): string[] => {
  if (includeBundleSourceProductColumns) return colIds;
  const strip = getBundleSourceProductColumnIdsFromItems(itemsForBundleDetection);
  return colIds.filter((id) => !strip.has(String(id)));
};

// Excel export: add bundle amounts on data rows; pinned summary rows already
// include bundle via applyPinnedRowsForExport / createPinnedRowDataNew sums.
// Also keep barcodes / long digit-only IDs as text (Excel Number type turns them
// into scientific notation and loses precision past ~15 digits).
const bundleProcessCellCallback = (params) => {
  let value = params?.value;
  const colId = String(params?.column?.getColId?.() ?? '').toLowerCase();
  if (!params?.node?.rowPinned) {
    if (colId && isProductQtyColId(colId)) {
      const add = bundleAdditionForProductQtyCol(getBundleAdditionsMap(params?.node?.data), colId);
      if (add) {
        const base = Number(params?.value);
        value = (Number.isNaN(base) ? 0 : base) + add;
      }
    }
  }
  const asStr = value == null ? '' : String(value).trim();
  if (colId.includes('barcode') || /^\d{16,}$/.test(asStr)) {
    return asStr;
  }
  return value;
};

const getEmptyColumns = (tableRef, columnsToExportIds) => {
  let columnsWithValue = {};
  tableRef?.current?.api?.forEachNodeAfterFilterAndSort((node) => {
    const additions = getBundleAdditionsMap(node.data);
    columnsToExportIds.forEach((col) => {
      if (node.data?.[col] || node.data?.[col] === false || bundleAdditionForProductQtyCol(additions, col)) {
        if (!columnsWithValue?.[col]) {
          columnsWithValue = {
            ...columnsWithValue,
            [col]: true,
          };
        }
      }
    });
  });
  const emptyColumns = columnsToExportIds.filter((col) => !columnsWithValue?.[col]);
  return emptyColumns;
}
const getEmptyColumnsData = (data, columnsToExportIds) => {
  let columnsWithValue = {};
  data.forEach((node) => {
    const additions = getBundleAdditionsMap(node.data);
    columnsToExportIds.forEach((col) => {
      if (node.data?.[col] || node.data?.[col] === false || bundleAdditionForProductQtyCol(additions, col)) {
        if (!columnsWithValue?.[col]) {
          columnsWithValue = {
            ...columnsWithValue,
            [col]: true,
          };
        }
      }
    });
  });
  const emptyColumns = columnsToExportIds.filter((col) => !columnsWithValue?.[col]);
  return emptyColumns;
}

const prependData = (columnsToExportIds, tableRef,tableControls) => {
  const data = columnsToExportIds?.map((col) => {

    return {
      data: {
        value: tableControls?.excelPrependTop ? tableControls?.excelPrependTop(col) :tableRef?.current?.api?.getColumnDef(col)?.headerName, // translate gere
      }
    }
  });
  return [
    {
      cells: data,
    }
  ]
}
/** Same value as the column in the grid (getValue / valueGetter / filterValueGetter), not only `data[field]`. */
const getSplitKeyFromNode = (api: any, colDef: any, fieldFallback: string, node: any) => {
  if (!node) return "";
  try {
    if (typeof api?.getValue === "function") {
      const gv = api.getValue(fieldFallback, node);
      if (gv === undefined || gv === null) return "";
      return String(gv);
    }
  } catch {
    /* fall through */
  }
  const data = node?.data;
  if (!data && !node?.group) return "";
  const params: any = {
    data: node?.data,
    node,
    api,
    colDef: colDef ?? {},
    column: null,
    getValue: () => undefined,
  };
  try {
    if (colDef && typeof colDef.filterValueGetter === "function") {
      const v = colDef.filterValueGetter(params);
      if (v === undefined || v === null) return "";
      return String(v);
    }
    if (colDef && typeof colDef.valueGetter === "function") {
      const v = colDef.valueGetter(params);
      if (v === undefined || v === null) return "";
      return String(v);
    }
  } catch {
    /* fall through */
  }
  const raw = data?.[fieldFallback];
  if (raw === undefined || raw === null) return "";
  return String(raw);
};

const excelSafeSheetName = (name: string) => {
  const base = (name || "ריק").replace(/[\\/*?:\[\]]/g, " ").trim() || "ריק";
  return base.length > 31 ? base.slice(0, 31) : base;
};

const snapshotPinnedRowData = (api: any) => {
  const top: any[] = [];
  const topCount = api?.getPinnedTopRowCount?.() ?? 0;
  for (let i = 0; i < topCount; i++) {
    const n = api.getPinnedTopRow(i);
    if (n?.data) top.push({ ...n.data });
  }
  const bottom: any[] = [];
  const botCount = api?.getPinnedBottomRowCount?.() ?? 0;
  for (let i = 0; i < botCount; i++) {
    const n = api.getPinnedBottomRow(i);
    if (n?.data) bottom.push({ ...n.data });
  }
  return { top, bottom };
};

const applyPinnedRowData = (api: any, top: any[], bottom: any[]) => {
  api?.setPinnedTopRowData?.(top?.length ? top.map((r) => ({ ...r })) : null);
  api?.setPinnedBottomRowData?.(bottom?.length ? bottom.map((r) => ({ ...r })) : null);
};

const collectExportRowData = (api: any, selectedRows: any[]): any[] => {
  if (selectedRows?.length) return selectedRows;
  const rows: any[] = [];
  api?.forEachNodeAfterFilterAndSort((node: any) => {
    if (node.group || node.rowPinned || !node.data) return;
    rows.push(node.data);
  });
  return rows;
};

/** Rebuild pinned summary rows for Excel only — sums include bundle roll-ups. */
const applyPinnedRowsForExport = (api: any, pinnedExportContext: any, rowData: any[]) => {
  const { top, bottom } = buildPinnedRowBlocksFromData(
    pinnedExportContext.columns,
    rowData,
    pinnedExportContext.pinnedControl,
    pinnedExportContext.pinnedTopRowDataConfiguration,
    pinnedExportContext.pinnedBottomRowDataConfiguration,
    true
  );
  applyPinnedRowData(api, top, bottom);
};

const DEFAULT_EXTRA_OPTIONS = {
  divarge: undefined as string | undefined,
  ignoreEmptyColumns: false,
  includeBundleSourceProductColumns: false,
  /** Embed product images in the xlsx (slower; prefetches base64 before export). */
  includeImages: false,
  fontSize: 12,
  rowHeight: 25,
  prependContent: false,
};

const IMAGE_EXPORT_MIN_ROW_HEIGHT = 56;

const ExcelExportModal = ({ open, setOpen, tableRef, tableControls, pinnedExportContext }) => {

  const [columnsToExport, setColumnsToExport] = useState<{
    headerName: string;
    id: string;
    column: any,
    checked: boolean;
  }[]>([]);

  const [columnToDivarge, setColumnToDivarge] = useState<{
    headerName: string;
    id: string;
    column: any,
    checked: boolean;
  } | null>(null);

  const [extraOptions, setExtraOptions] = useState<{
    divarge?: string;
    ignoreEmptyColumns?: boolean;
    /** When true, export `p_X` / `rrp_X` for products that are bundle sources (`aap_X`). Default false = omit those columns; amounts still roll to bundled products. */
    includeBundleSourceProductColumns?: boolean;
    includeImages?: boolean;
    fontSize: number;
    rowHeight: number;
    prependContent?: boolean;
  }>({ ...DEFAULT_EXTRA_OPTIONS });

  const [fileName, setFileName] = useState<string>("");
  const [openMore, setOpenMore] = useState<boolean>(false);
  const [exportBusy, setExportBusy] = useState(false);
  /** Bumps when the menu opens so MUI `Select` remounts and drops internal/menu state from the last open. */
  const [selectMountKey, setSelectMountKey] = useState(0);

  const hasImageColumn = columnsToExport.some((c) => isImageExportColumn(c.id, c.headerName));

  useEffect(() => {
    const tableHead = document?.querySelector(".ag-table-name-head");
    if (!tableHead) {
      return;
    }
    const tableHeadText = tableHead?.textContent;
    if (!tableHeadText) {
      return;
    }
    setFileName(tableHeadText);
  }, [open]);

  useEffect(() => {
    const allColumns = tableRef?.current?.columnApi?.getAllGridColumns();
    if (!allColumns) {
      return;
    }
    setColumnsToExport(allColumns.map((column) => ({
      headerName: column.getColDef().headerName,
      id: column.colId,
      checked: column.isVisible(),
    })));
  }, [tableRef, open]);

  const onExport = async  () => {
    if (exportBusy) return;
    let columnsToExportIds = columnsToExport.filter((col) => col.checked).map((col) => col.id);

    const api = tableRef?.current?.api;
    const selectedRows = getSelectedRows(api);
    const exportRowData = collectExportRowData(api, selectedRows);

    const usePinnedForExport =
      Boolean(pinnedExportContext?.pinnedControl?.pinnedRowControlsNew) && !tableControls?.noPinned;

    let savedPinned: { top: any[]; bottom: any[] } | null = null;
    if (usePinnedForExport && api) {
      savedPinned = snapshotPinnedRowData(api);
      applyPinnedRowsForExport(api, pinnedExportContext, exportRowData);
    }

    // Gather the row set this export will see so we can discover bundle
    // destination columns (p_Y from aap_X) that need to be auto-added.
    const bundleSourceItems: any[] = [];
    if (!extraOptions.divarge) {
      if (selectedRows?.length) {
        selectedRows.forEach((d) => bundleSourceItems.push(d));
      } else {
        api?.forEachNodeAfterFilterAndSort((n) => bundleSourceItems.push(n));
      }
    } else {
      api?.forEachNodeAfterFilterAndSort((n) => bundleSourceItems.push(n));
    }
    columnsToExportIds = getBundleAugmentedColumnKeys(tableRef, bundleSourceItems, columnsToExportIds);

    if (extraOptions.ignoreEmptyColumns) {
      const emptyColumns = getEmptyColumns(tableRef, columnsToExportIds);
      if (emptyColumns.length) {
        columnsToExportIds = columnsToExportIds.filter((col) => {
          if (extraOptions.includeImages) {
            const colMeta = columnsToExport.find((c) => c.id === col);
            const def = api?.getColumnDef?.(col);
            if (isImageExportColumn(col, colMeta?.headerName ?? def?.headerName)) return true;
          }
          return !emptyColumns.includes(col);
        });
      }
    }
    columnsToExportIds = applyBundleSourceProductColumnExportPreference(
      columnsToExportIds,
      bundleSourceItems,
      !!extraOptions.includeBundleSourceProductColumns
    );

    // If "with images" is on, ensure image columns are part of the export set.
    if (extraOptions.includeImages) {
      for (const col of columnsToExport) {
        if (!isImageExportColumn(col.id, col.headerName)) continue;
        if (!columnsToExportIds.includes(col.id)) columnsToExportIds.push(col.id);
      }
    }

    const imageColIds = new Set(
      columnsToExportIds.filter((id) => {
        const colMeta = columnsToExport.find((c) => c.id === id);
        const def = api?.getColumnDef?.(id);
        return isImageExportColumn(id, colMeta?.headerName ?? def?.headerName);
      })
    );
    const includeImages = !!extraOptions.includeImages && imageColIds.size > 0;

    let imageByProductId = new Map<string, ExcelExportImage>();
    setExportBusy(true);
    try {
      if (includeImages) {
        imageByProductId = await buildExcelImageMapByProductId(exportRowData);
      }

      const exportRowHeight = includeImages
        ? Math.max(Number(extraOptions.rowHeight) || 0, IMAGE_EXPORT_MIN_ROW_HEIGHT)
        : extraOptions.rowHeight;

      const processCellCallback = (params: any) => {
        const colId = String(params?.column?.getColId?.() ?? '');
        if (includeImages && imageColIds.has(colId) && !params?.node?.rowPinned) {
          // Pass the resolved base64 as the cell value so addImageToCell cannot
          // look up the wrong product (no secondary Map lookup by id).
          const key = excelImageKeyForRow(params?.node?.data);
          if (!key) return '';
          return imageByProductId.get(key)?.base64 ?? '';
        }
        return bundleProcessCellCallback(params);
      };

      const addImageToCell = includeImages
        ? (rowIndex: number, column: any, value: string) => {
            const colId = String(column?.getColId?.() ?? column?.getColDef?.()?.field ?? '');
            if (!imageColIds.has(colId)) return;
            const raw = String(value ?? '').trim();
            if (!raw.startsWith('data:image')) return;
            const parsed = parseDataUrl(raw);
            if (!parsed) return;
            // Unique id per cell — AG Grid mutates image.position on the object.
            // Fixed size inside the same cell (no fitCell + width, which drifts).
            return {
              image: {
                id: `product_img_r${rowIndex}_${colId}`,
                base64: parsed.base64,
                imageType: parsed.imageType,
                width: 40,
                height: 40,
                position: {
                  offsetX: 6,
                  offsetY: 6,
                },
              },
              value: '',
            };
          }
        : undefined;

      const columnWidth = includeImages
        ? (params: { column?: any; index: number }) => {
            const colId = String(params?.column?.getColId?.() ?? '');
            if (imageColIds.has(colId)) return 70;
            const w = params?.column?.getActualWidth?.();
            return Math.max(typeof w === 'number' ? w : 75, 75);
          }
        : undefined;

      const defaultExportParams = {
        columnKeys: columnsToExportIds,
        sheetName: 'נתונים',
        fileName: `סולביט - ${fileName}`,
        fontSize: extraOptions.fontSize,
        rowHeight: exportRowHeight,
        columnWidth,
        rowGroupExpandState: tableControls?.groupBy?.removeSingleChildren ? 'expanded':'collapsed',
        prependContent: extraOptions?.prependContent ? prependData(columnsToExportIds, tableRef,tableControls) : undefined,
        processCellCallback,
        addImageToCell,
      }
      if (extraOptions.divarge) {
        const divarge = extractBeforeNumber(extraOptions.divarge)
        const defs = api.getColumnDefs() || [];
        const flattenColDefs = (arr: any[]): any[] =>
          arr.flatMap((c: any) => (c?.children ? flattenColDefs(c.children) : [c]));
        const col =
          api.getColumnDef(divarge) ??
          api.getColumnDef(extraOptions.divarge) ??
          flattenColDefs(defs).find((c: any) => c.field === divarge || c.colId === divarge);

        const splitKeys = new Set<string>();
        api.forEachNodeAfterFilterAndSort((node: any) => {
          if (node.group || node.rowPinned || !node.data) return;
          splitKeys.add(getSplitKeyFromNode(api, col, divarge, node));
        });

        var spreadsheets = [tableRef?.current?.api?.getSheetDataForExcel({ ...defaultExportParams, sheetName: 'כל הנתונים' })];
        if (!col) {
          tableRef.current!.api.exportMultipleSheetsAsExcel({
            data: spreadsheets,
            fileName: `סולביט - ${fileName}`,
          });
          return
        }

        const keysSorted = Array.from(splitKeys).sort((a, b) => a.localeCompare(b, "he"));

        const usePinnedForSplit = usePinnedForExport;

          for (const r of keysSorted) {
            const matchingNodes: any[] = [];
            api.forEachNodeAfterFilterAndSort((node: any) => {
              if (node.group || node.rowPinned || !node.data) return;
              if (getSplitKeyFromNode(api, col, divarge, node) !== r) return;
              matchingNodes.push(node);
            });

            let colmn = columnsToExport.filter((c) => c.checked).map((c) => c.id);
            colmn = getBundleAugmentedColumnKeys(tableRef, matchingNodes, colmn);
            if (extraOptions.ignoreEmptyColumns) {
              const emptyColumns = getEmptyColumnsData(matchingNodes, colmn);
              if (emptyColumns.length) {
                colmn = colmn.filter((c) => {
                  if (includeImages && imageColIds.has(c)) return true;
                  return !emptyColumns.includes(c);
                });
              }
            }
            colmn = applyBundleSourceProductColumnExportPreference(
              colmn,
              matchingNodes,
              !!extraOptions.includeBundleSourceProductColumns
            );
            if (includeImages) {
              for (const id of imageColIds) {
                if (!colmn.includes(id)) colmn.push(id);
              }
            }

            if (usePinnedForSplit) {
              const rowDataSubset = matchingNodes.map((n) => n.data).filter(Boolean);
              const { top: pinTop, bottom: pinBottom } = buildPinnedRowBlocksFromData(
                pinnedExportContext.columns,
                rowDataSubset,
                pinnedExportContext.pinnedControl,
                pinnedExportContext.pinnedTopRowDataConfiguration,
                pinnedExportContext.pinnedBottomRowDataConfiguration,
                true
              );
              applyPinnedRowData(api, pinTop, pinBottom);
            }

            const sheet = api.getSheetDataForExcel({
              ...defaultExportParams,
              columnKeys: colmn,
              sheetName: excelSafeSheetName(r),
              skipPinnedTop: !usePinnedForSplit,
              skipPinnedBottom: !usePinnedForSplit,
              shouldRowBeSkipped: (p) => {
                const node = p.node;
                if (node.rowPinned) {
                  if (!usePinnedForSplit) return true;
                  return false;
                }
                if (node.group) return true;
                if (!node.data) return true;
                return getSplitKeyFromNode(api, col, divarge, node) !== r;
              },
              prependContent: extraOptions?.prependContent ? prependData(colmn, tableRef, tableControls) : undefined,
            });
            if (sheet) {
              spreadsheets.push(sheet);
            }
          }

        tableRef.current!.api.exportMultipleSheetsAsExcel({
          data: spreadsheets,
          fileName: `סולביט - ${fileName}`,
        });
        spreadsheets = [];
      } else {
        if (selectedRows?.length) {
          api?.exportDataAsExcel({
            ...defaultExportParams,
            onlySelected: true,
          });
        } else {
          api?.exportDataAsExcel(defaultExportParams);
        }
      }
    } finally {
      setExportBusy(false);
      if (usePinnedForExport && savedPinned && api) {
        applyPinnedRowData(api, savedPinned.top, savedPinned.bottom);
      }
    }
  }

  const setPosition = () => {
    const divToAttach = "export-to-excel-icon";
    const div = document.getElementById(divToAttach);
    if (!div) {
      return;
    }
    const rect = div.getBoundingClientRect();
    const top = rect.top;
    const select = document.getElementById('columns-select');
    if (!select) {
      return;
    }

    return {
      top: `${top}px`,
    }
  }

  const { top } = setPosition() || {};

  const [openPopover, setOpenPopover] = useState<boolean>(false);
  const [anchorElPopover, setAnchorElPopover] = useState<null | HTMLElement>(null);

  useEffect(() => {
    if (!open) return;
    setSelectMountKey((k) => k + 1);
    setColumnToDivarge(null);
    setExtraOptions({ ...DEFAULT_EXTRA_OPTIONS });
    setOpenMore(false);
    setOpenPopover(false);
    setAnchorElPopover(null);
    setExportBusy(false);
  }, [open]);

  const handleClose = () => {
    setOpen(false)
    setOpenPopover(false)
    setAnchorElPopover(null)
  }

  return (
    <div style={{
      position: 'relative',
    }}>
      <Select
        key={selectMountKey}
        id="columns-select"
        open={open}
        sx={{
          position: 'absolute',
          opacity: 0,
          top: -40,
          zIndex: -1,
        }}
        MenuProps={{
          onClose: handleClose,
          style: {
            maxHeight: '450px',
          }
        }}
      >
        <List>

          <ListItem>
            <TextField
              variant="standard" label="שם קובץ" value={fileName} onChange={(e) => setFileName(e.target.value)} onKeyDown={(e) => e.stopPropagation()} />
          </ListItem>

          <ListItemButton
            disabled={exportBusy}
            style={{
            position: 'sticky',
            top: 0,
            zIndex: 1,
            backgroundColor: '#e0ffd8',
            borderBottom: '2px solid #e0e0e0',
            opacity: exportBusy ? 0.7 : 1,
          }} onClick={() => onExport()}>
            <ListItemText primary={exportBusy ? (extraOptions.includeImages ? 'טוען תמונות ומייצא…' : 'מייצא…') : 'ייצא'} />
            <ListItemIcon>
              <Avatar variant='square'
                sx={{ cursor: 'pointer', bgcolor: '#f8f8f8', width: '30px', height: '30px', mr: 1 }}
                src={xslx} />

            </ListItemIcon>
          </ListItemButton>

          <ListItemButton onClick={() => setOpenMore(!openMore)} style={{
            height: '40px',
            borderBottom: '2px solid #e0e0e0',
          }}>
            <ListItemText>
              אפשרויות ייצוא
            </ListItemText>
            <ListItemIcon>
              {
                openMore ?
                  <ExpandLess /> :
                  <ExpandMore />
              }
            </ListItemIcon>
          </ListItemButton>

          <Collapse
            in={openMore}
          // onClick={(e) => e.stopPropagation()}
          >
            <MenuItem
              onClick={(e) => {
                if (openPopover) {
                  setOpenPopover(false);
                  setAnchorElPopover(null);
                  return;
                } else {
                  setOpenPopover(true);
                  setAnchorElPopover(e.currentTarget);
                }
              }}
            >
              <ListItemText>
                פצל קבוצות ללשונייות <span style={{
                  color: 'grey',
                  marginRight: '5px',
                }}>{columnToDivarge?.headerName ? columnToDivarge?.headerName : ''}</span>
              </ListItemText>
              <Popover
                id="mouse-over-popover"
                open={openPopover && Boolean(anchorElPopover)}
                anchorEl={anchorElPopover}
                anchorOrigin={{
                  vertical: 'center',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'center',
                  horizontal: 'left',
                }}
              >
                {columnsToExport.map((column) => (
                  <ListItem key={column.id + "-divarge"} value={column.id}>
                    <Checkbox onChange={(e) => {
                      // e.stopPropagation();
                      if (e.target.checked) {
                        setColumnToDivarge(column);
                        setExtraOptions({ ...extraOptions, divarge: column?.id });
                      } else {
                        setColumnToDivarge(null);
                        setExtraOptions({ ...extraOptions, divarge: undefined });
                      }
                    }} checked={columnToDivarge?.id === column.id} />
                    <ListItemText primary={column.headerName} />
                  </ListItem>
                ))}
              </Popover>
            </MenuItem>
            <MenuItem>
              <ListItemText>
                התעלם מעמודות ריקות
              </ListItemText>
              <Checkbox checked={!!extraOptions.ignoreEmptyColumns} onChange={(e) => {
                setExtraOptions({ ...extraOptions, ignoreEmptyColumns: e.target.checked });
              }} />
            </MenuItem>
            <MenuItem>
              <ListItemText>
                כלול עמודות מוצר-מקור באנדל חבילה (עמודת המארז עצמה, לדוגמה p_1)
              </ListItemText>
              <Checkbox checked={!!extraOptions.includeBundleSourceProductColumns} onChange={(e) => {
                setExtraOptions({ ...extraOptions, includeBundleSourceProductColumns: e.target.checked });
              }} />
            </MenuItem>
            {hasImageColumn && (
              <MenuItem>
                <ListItemText>
                  עם תמונות (איטי יותר)
                </ListItemText>
                <Checkbox
                  checked={!!extraOptions.includeImages}
                  disabled={exportBusy}
                  onChange={(e) => {
                    setExtraOptions({ ...extraOptions, includeImages: e.target.checked });
                  }}
                />
              </MenuItem>
            )}
            <MenuItem>
              <ListItemText>
                הוסף תרגום לראשי עמודות
              </ListItemText>
              <Checkbox checked={!!extraOptions.prependContent} onChange={(e) => {
                setExtraOptions({ ...extraOptions, prependContent: e.target.checked });
              }} />
            </MenuItem>
            <MenuItem>
              <TextField label="גודל פונט" value={extraOptions.fontSize} onChange={(e) => setExtraOptions({ ...extraOptions, fontSize: Number(e.target.value) })} variant="standard" />
            </MenuItem>
            <MenuItem>
              <TextField label="גובה שורה" value={extraOptions.rowHeight} onChange={(e) => setExtraOptions({ ...extraOptions, rowHeight: Number(e.target.value) })} variant="standard" />
            </MenuItem>
          </Collapse>

          <ListItem>
            <ListItemText>
              בחר את העמודות שברצונך לייצא
            </ListItemText>
          </ListItem>

          <ListItemButton onClick={() => {
            const isAllSelected = columnsToExport.every((col) => col.checked);
            if (isAllSelected) {
              setColumnsToExport(columnsToExport.map((col) => ({ ...col, checked: false })));
            } else {
              setColumnsToExport(columnsToExport.map((col) => ({ ...col, checked: true })));
            }
            setOpen(true)
          }}>
            <ListItemText primary={columnsToExport.every((col) => col.checked) ? 'בטל בחירה' : 'בחר הכל'} />
          </ListItemButton>

          {columnsToExport.map((column) => (
            <ListItem key={column.id} value={column.id}>
              <Checkbox onChange={(e) => {
                if (e.target.checked) {
                  setColumnsToExport(columnsToExport.map((col) => {
                    if (col.id === column.id) {
                      return { ...col, checked: true };
                    }
                    return col;
                  }
                  ));
                } else {
                  setColumnsToExport(columnsToExport.map((col) => {
                    if (col.id === column.id) {
                      return { ...col, checked: false };
                    }
                    return col;
                  }
                  ));
                }
              }} checked={column.checked} />
              <ListItemText primary={column.headerName} />
            </ListItem>
          ))}

        </List>
      </Select>
    </div>
  )
}

export default ExcelExportModal
